import React from 'react';
import { cn } from '@/utils/cn';

interface ProjectProgressGanttProps {
  data: any;
}

export function ProjectProgressGantt({ data }: ProjectProgressGanttProps) {
  const months = [
    { name: 'Tháng 4, 2024', days: [1, 6, 11, 16, 21, 26] },
    { name: 'Tháng 5, 2024', days: [1, 6, 11, 16, 21, 26], isCurrent: true },
    { name: 'Tháng 6, 2024', days: [1, 6, 11, 16, 21, 26] },
    { name: 'Tháng 7, 2024', days: [1, 6, 11, 16, 21, 26] },
  ];

  return (
    <div className="bg-white rounded-md border border-slate-100  overflow-hidden">
      <div className="p-4 border-b border-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
           <h3 className="text-sm font-normal text-slate-900 tracking-wider">Tiến độ theo hạng mục</h3>
           <i className="ri-information-line text-slate-400 cursor-help" title="Tiến độ thực tế so với kế hoạch" />
        </div>
        <div className="flex items-center gap-4">
           <div className="flex items-center gap-6">
              <LegendItem color="bg-emerald-500" label="Hoàn thành" />
              <LegendItem color="bg-blue-500" label="Đang thực hiện" />
              <LegendItem color="bg-slate-300" label="Chưa bắt đầu" />
              <LegendItem color="bg-rose-500" label="Chậm tiến độ" />
           </div>
           <div className="flex items-center border border-slate-200 rounded-md overflow-hidden h-8">
              <button className="px-2 h-full flex items-center justify-center hover:bg-slate-50 border-r border-slate-100"><i className="ri-zoom-in-line text-slate-400" /></button>
              <button className="px-2 h-full flex items-center justify-center hover:bg-slate-50 border-r border-slate-100"><i className="ri-zoom-out-line text-slate-400" /></button>
              <button className="px-2 h-full flex items-center justify-center hover:bg-slate-50"><i className="ri-fullscreen-line text-slate-400" /></button>
           </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[1000px]">
          {/* Header Timeline */}
          <div className="grid grid-cols-[280px_1fr] border-b border-slate-100">
            <div className="p-3 bg-slate-50/50 text-xs font-normal text-slate-400 tracking-wider">Hạng mục</div>
            <div className="flex">
              {months.map((month, mIdx) => (
                <div key={mIdx} className="flex-1 border-l border-slate-100">
                  <div className="text-center py-2 text-xs font-normal text-slate-500 border-b border-slate-50">{month.name}</div>
                  <div className="flex">
                    {month.days.map(day => (
                      <div key={day} className="flex-1 text-center py-1 text-xs font-normal text-slate-400 border-l first:border-0 border-slate-50/50">{day.toString().padStart(2, '0')}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gantt Rows */}
          <div className="divide-y divide-slate-50">
            {data.ganttItems.map((item: any, idx: number) => (
              <GanttRow key={item.id} item={item} />
            ))}
            
            {/* Row Tổng cộng */}
            <div className="grid grid-cols-[280px_1fr] bg-slate-50/30">
               <div className="p-4 flex justify-between items-center">
                  <span className="text-xs font-normal text-slate-900 tracking-wider">Tổng cộng</span>
                  <span className="text-xs font-normal text-slate-900">{data.overallProgress}%</span>
               </div>
               <div className="flex"></div>
            </div>
          </div>

          {/* Time Marker */}
          <div className="relative h-2">
             <div className="absolute top-[-400px] bottom-0 left-[45%] w-0.5 bg-emerald-500/30 z-10">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-full px-2 py-1 bg-emerald-500 text-white text-xs font-normal rounded-t-md whitespace-nowrap">
                   Hôm nay 15/05
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GanttRow({ item }: { item: any }) {
  // Logic tính toán vị trí thanh tiến độ dựa trên ngày (giả lập đơn giản)
  const getPosition = (id: string) => {
    const positions: any = {
      '1': { left: '0%', width: '38%', color: 'bg-emerald-500' },
      '2': { left: '18%', width: '42%', color: 'bg-blue-500' },
      '3': { left: '32%', width: '30%', color: 'bg-amber-500' },
      '4': { left: '32%', width: '10%', color: 'bg-rose-500' },
      '5': { left: '68%', width: '25%', color: 'bg-slate-300' },
    };
    return positions[id] || { left: '0%', width: '0%', color: 'bg-slate-300' };
  };

  const pos = getPosition(item.id);

  return (
    <div className="grid grid-cols-[280px_1fr] group hover:bg-slate-50 transition-colors">
      <div className="p-4 flex items-center justify-between border-r border-slate-50">
        <div className="flex items-center gap-2">
           <i className="ri-arrow-right-s-line text-slate-400 group-hover:text-emerald-500 transition-colors" />
           <span className="font-normal text-slate-700 truncate">{item.name}</span>
        </div>
        <div className="flex items-center gap-2">
           <div className={cn("w-2 h-2 rounded-full", pos.color)} />
           <span className="text-xs font-normal text-slate-900">{item.progress}%</span>
        </div>
      </div>
      <div className="relative h-12 flex items-center px-4">
        {/* Background Grid Lines (Simplified) */}
        <div className="absolute inset-0 flex">
           {Array.from({ length: 4 }).map((_, i) => <div key={i} className="flex-1 border-r border-slate-50 last:border-0" />)}
        </div>
        
        {/* Progress Bar */}
        <div 
          className={cn("absolute h-4 rounded-full  flex items-center justify-end px-2 group-hover:brightness-110 transition-all", pos.color)}
          style={{ left: pos.left, width: pos.width }}
        >
           <span className="text-xs font-normal text-white drop-">{item.progress}%</span>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className={cn("w-3 h-1 rounded-full", color)} />
      <span className="text-xs font-normal text-slate-400">{label}</span>
    </div>
  );
}
